<template>
    <SearchFilters
        v-model:institution-type="filters.institutionType"
        v-model:university="filters.university"
        v-model:course="filters.course"
        v-model:school="filters.school"
        v-model:subject="filters.subject"
        v-model:language="filters.language"
        v-model:study-material-types="filters.studyMaterialTypes"
        v-model:ai-only="filters.aiOnly"
        v-model:academic-year="filters.academicYear"
        v-model:fullscreen-opened="opened"
        :fullscreen="maxMD"
        :languages="allLanguages"
        @reset="resetFilters"
    />
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue';
import { debounce, cloneDeep, isEqual } from 'lodash-es';
import SearchFilters from '@/components/SearchFilters';
import type {
    UniversityBaseResource as UniversityResource,
    InstitutionResource as SchoolResource,
} from '@/api/backend/resources/UniversityResource';
import type { SearchCourseResource } from '@/api/gateway/legacy-api/resources/SearchCourseResource';
import { InstitutionType } from '@/api/gateway/legacy-api/resources/UniversityResource';
import { type StudyMaterialTypeId, ACADEMIC_YEAR_MIN, ACADEMIC_YEAR_MAX } from '@/utils/searchUtils';
import { getUniversityById } from '@/api/backend/university';
import { getCourseById } from '@/api/backend/course';
import { useScreenSize } from '@/composables/screenSize';
import type { LanguageResource } from '@/api/gateway/legacy-api/resources/LanguageResource';
import { fetchLanguages } from '@/api/gateway/legacy-api';
import { useTranslation } from 'i18next-vue';
import type { SearchFilters as SearchFiltersType } from './SearchIndex';

type CourseResource = Pick<SearchCourseResource, 'id' | 'name'>;

/**
 * This module controls the search filters.
 *
 * The module debounces the filter update events. The event
 * will not be emitted when changes are the same as the last
 * emit.
 */
export default defineComponent({
    name: 'SearchFiltersWrapper',
    components: {
        SearchFilters,
    },
    props: {
        /**
         * The filters that are usable for search endpoints.
         */
        modelValue: {
            type: Object as PropType<SearchFiltersType>,
            required: true,
        },
    },
    emits: {
        'update:model-value': (filters: SearchFiltersType) => true, // eslint-disable-line
    },

    setup: () => {
        const { t } = useTranslation('search');
        const { maxMD } = useScreenSize();
        return {
            maxMD,
            initializing: false,
            defaultState: {},
            previousFilters: {} as SearchFiltersType,
            emitFiltersDebounced: undefined as ReturnType<typeof debounce<() => void>> | undefined,
            t
        };
    },
    data: () => ({
        opened: false,
        allLanguages: [] as LanguageResource[],
        filters: {
            institutionType: InstitutionType.UNIVERSITY as InstitutionType,
            university: undefined as UniversityResource | undefined,
            course: undefined as CourseResource | undefined,
            school: undefined as SchoolResource | undefined,
            language: {} as LanguageResource,
            subject: undefined as CourseResource | undefined,
            studyMaterialTypes: [] as StudyMaterialTypeId[],
            aiOnly: false,
            academicYear: [
                ACADEMIC_YEAR_MIN,
                ACADEMIC_YEAR_MAX,
            ],
        },
    }),
    watch: {
        filters: {
            handler(): void {
                if (this.initializing) return;
                this.emitFiltersDebounced?.();
            },
            deep: true,
        },
        modelValue: {
            handler(): void {
                this.initializeFilters();
            },
        },
    },
    async mounted() {
        this.emitFiltersDebounced = debounce(this.emitFilters, 500);
        const languages = await fetchLanguages();
        this.filters.language = { id: 0, name: this.t('filter.all'), code: '', is_top: true };
        this.defaultState = cloneDeep(this.filters);
        languages.unshift(this.filters.language);
        this.allLanguages = languages;
        this.initializeFilters();
    },
    methods: {
        async initializeFilters(): Promise<void> {
            if (isEqual(this.modelValue, this.makeFilters())) return;
            this.initializing = true;
            await this.loadFilters();
            this.previousFilters = cloneDeep(this.makeFilters());
            this.initializing = false;
        },
        async loadFilters(): Promise<void> {
            if (this.modelValue.university_id) {
                await getUniversityById(this.modelValue.university_id).then((response) => {
                    const { id, name, institution_type } = response.data;
                    if (institution_type === InstitutionType.UNIVERSITY) {
                        this.filters.institutionType = InstitutionType.UNIVERSITY;
                        this.filters.university = { id, name };
                    }
                    if (institution_type === InstitutionType.SCHOOL) {
                        this.filters.institutionType = InstitutionType.SCHOOL;
                        this.filters.school = { id, name, institution_type };
                    }
                });
            }
            if (this.modelValue.course_id) {
                await getCourseById(this.modelValue.course_id).then((response) => {
                    const { id, name } = response.data;
                    if (this.filters.institutionType === InstitutionType.UNIVERSITY) {
                        this.filters.course = { id, name };
                    }
                    if (this.filters.institutionType === InstitutionType.SCHOOL) {
                        this.filters.subject = { id, name };
                    }
                });
            }
            this.filters.language =
                this.allLanguages.find((l) => l.id === this.modelValue.language_id) ?? this.allLanguages[0];
            if (this.modelValue.type_ids) {
                this.filters.studyMaterialTypes = this.modelValue.type_ids.map((id) => Number(id));
            }
            if (this.modelValue.has_ai_content) {
                this.filters.aiOnly = this.modelValue.has_ai_content;
            }
            this.filters.institutionType = this.modelValue.institution_type ? 1 : 0;
            if (this.modelValue.min_academic_year) {
                this.filters.academicYear = [
                    this.modelValue.min_academic_year,
                    this.filters.academicYear[1],
                ];
            }
            if (this.modelValue.max_academic_year) {
                this.filters.academicYear = [
                    this.filters.academicYear[0],
                    this.modelValue.max_academic_year,
                ];
            }
        },
        makeFilters(): SearchFiltersType {
            const filters = {} as SearchFiltersType;
            filters.university_id =
                this.filters.institutionType === InstitutionType.UNIVERSITY
                    ? this.filters.university?.id
                    : this.filters.institutionType === InstitutionType.SCHOOL
                      ? this.filters.school?.id
                      : undefined;
            filters.course_id =
                this.filters.institutionType === InstitutionType.UNIVERSITY
                    ? this.filters.course?.id
                    : this.filters.institutionType === InstitutionType.SCHOOL
                      ? this.filters.subject?.id
                      : undefined;
            filters.type_ids =
                this.filters.studyMaterialTypes.length === 0
                    ? undefined
                    : [...this.filters.studyMaterialTypes].sort((a, b) => a - b);
            filters.language_id = this.filters.language?.id ?? 0;
            filters.has_ai_content = this.filters.aiOnly ? true : undefined;
            filters.min_academic_year =
                this.filters.academicYear[0] === ACADEMIC_YEAR_MIN ? undefined : this.filters.academicYear[0];
            filters.max_academic_year =
                this.filters.academicYear[1] === ACADEMIC_YEAR_MAX ? undefined : this.filters.academicYear[1];
            filters.institution_type = this.filters.institutionType;
            return filters;
        },
        emitFilters(): void {
            const filters = this.makeFilters();
            if (!isEqual(this.previousFilters, filters)) {
                this.previousFilters = cloneDeep(filters);
                this.$emit('update:model-value', filters);
            }
        },
        resetFilters(): void {
            for (const [key, value] of Object.entries(this.defaultState)) {
                // @ts-ignore: unmatched type due to dynamic indexing
                this.filters[key] = cloneDeep(value);
            }
        },
        popupFilters(): void {
            if (!this.maxMD) return;
            this.opened = true;
        },
    },
});
</script>
